'use client';

import { FC, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  TextField,
  Button,
  Tabs,
  Tab,
  Paper,
  Stack,
} from '@mui/material';
import { useRouter } from 'next/navigation';
import { StandardDialog } from '@/components/common/StandardDialog';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useBackendStore } from '@/stores/backendStore';
import { useDialogStore, type ShippingDialogLaunchFrom } from '@/stores/dialogStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { ShippingRequest, ProjectDto, ShippingDto, StatusDto, SampleWithLabelDto } from '@/api/ApiClient';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { log } from '@/lib/utils/logger';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { focusTreeNodeInEditMode } from '@/lib/tree/treeNodeEditFocus';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useAuthStore } from '@/stores/authStore';
import { isExternalProject } from '@/lib/types/projectClassification';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { ShippingSampleSelector } from '@/components/details/shipping/ShippingSampleSelector';
import { ShippingTypeSelect } from '@/components/details/shipping/ShippingTypeSelect';
import { OptionKey, type OptionMap } from '@/lib/options/optionTypes';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { getProblemDetail } from '@/lib/utils/apiErrors';

const OPTION_KEYS: OptionKey[] = ['projects', 'shippingLabelTypes', 'shippingTypes'];

interface AddShippingDialogProps {
  open: boolean;
  onClose: () => void;
  /** Opening project context (tree). Omit when starting from the global shippings page—user picks a target inside the dialog. */
  project?: ProjectDto;
  returnAddress?: string;
  /** Where this flow was launched from (copy + sample picker defaults only). */
  launchFrom?: ShippingDialogLaunchFrom;
  onSubmit?: (shipping: ShippingDto) => void;
}

type RecipientType = 'inHouse' | 'externalUser';

type AddShippingDialogOptions = Partial<Pick<OptionMap, 'projects' | 'shippingLabelTypes' | 'shippingTypes'>>;

interface AddShippingDialogInnerProps extends AddShippingDialogProps {
  options: AddShippingDialogOptions;
}

const AddShippingDialogInner: FC<AddShippingDialogInnerProps> = ({
  open,
  onClose,
  project,
  returnAddress: initialReturnAddress,
  launchFrom = 'projectTree',
  onSubmit,
  options,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const systemConfig = useSystemConfig();
  const router = useRouter();
  const { showProjectSelectionDialog } = useDialogStore();
  const { hasUnsavedChanges, markAsInteracted, reset } = useUserInteraction();
  const { createNode, expandNode } = useTree();
  const { selectedNode } = useTreeSelection();
  const { currentUser } = useAuthStore();

  const isCurrentProjectExternal = !!(project && isExternalProject(project));

  // Form state - initialize returnAddress from prop
  const [isLocalTransfer, setIsLocalTransfer] = useState<boolean>(false);
  const [returnAddress, setReturnAddress] = useState<string>(initialReturnAddress || '');
  const [recipientType, setRecipientType] = useState<RecipientType>(() =>
    project && isExternalProject(project) ? 'externalUser' : 'inHouse'
  );
  const [targetProject, setTargetProject] = useState<{ id: number; label: string } | null>(() =>
    project?.id != null && project.projectLabel ? { id: project.id, label: project.projectLabel } : null
  );
  const [samples, setSamples] = useState<SampleWithLabelDto[]>([]);
  const [tab, setTab] = useState(0);
  const [researcherPhone, setResearcherPhone] = useState('');
  const [budget, setBudget] = useState('');
  const [shippingType, setShippingType] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [comment, setComment] = useState('');

  // External project options for the recipient project lookup (only external projects)
  const externalProjectOptions =
    options.projects
      ?.filter((p) => p.id && p.projectLabel && !p.type?.isInternal)
      ?.map((p) => ({ id: p.id!, label: p.projectLabel! })) ?? [];

  const internalProjectOptions = useMemo(
    () =>
      options.projects
        ?.filter((p) => p.id && p.projectLabel && p.type?.isInternal)
        ?.map((p) => ({ id: p.id!, label: p.projectLabel! })) ?? [],
    [options.projects]
  );

  const targetProjectOptions = recipientType === 'externalUser' ? externalProjectOptions : internalProjectOptions;

  const pendingRequestLabel = isLocalTransfer ? 'pending transfer request' : 'pending shipping request';

  const handleTargetProjectLookup = useCallback(async () => {
    if (recipientType === 'externalUser') {
      const projects = await showProjectSelectionDialog('Select External User Project');
      if (projects && projects.length > 0) {
        const p = projects[0];
        setTargetProject(p.id && p.projectLabel ? { id: p.id, label: p.projectLabel } : null);
        markAsInteracted();
      }
      return;
    }
    const projects = await showProjectSelectionDialog('Select target project');
    if (projects && projects.length > 0) {
      const p = projects[0];
      if (p.id && p.projectLabel) {
        if (isExternalProject(p)) {
          showSnackbar('For in-house collaboration, pick an internal project as the target.', 'warning');
          return;
        }
        setTargetProject({ id: p.id, label: p.projectLabel });
        markAsInteracted();
      }
    }
  }, [recipientType, showProjectSelectionDialog, markAsInteracted, showSnackbar]);

  // Reset form to defaults when dialog closes
  const handleClose = () => {
    setIsLocalTransfer(false);
    setReturnAddress('');
    setRecipientType(project && isExternalProject(project) ? 'externalUser' : 'inHouse');
    setTargetProject(
      project?.id != null && project.projectLabel ? { id: project.id, label: project.projectLabel } : null
    );
    setSamples([]);
    setTab(0);
    setResearcherPhone('');
    setBudget('');
    setShippingType('');
    setContactName('');
    setContactEmail('');
    setContactPhone('');
    setAddress('');
    setComment('');
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      if (!currentUser) {
        showSnackbar('User information not available', 'error');
        return;
      }

      const filingProjectId = targetProject?.id ?? project?.id;
      if (filingProjectId == null) {
        showSnackbar('Choose a target project before creating this shipping.', 'error');
        return;
      }

      // Create a new shipping with required fields
      const newShipping: ShippingRequest = {
        projectId: filingProjectId,
        localTransfer: isLocalTransfer,
        // Researcher fields (current user)
        researcher: currentUser.id,
        researcherEmail: currentUser.email || currentUser.eppn || undefined,
        researcherPhone: researcherPhone || undefined,
        // Status
        status: StatusDto.Pending,
        // Return address (fetched from backend)
        returnAddress: returnAddress || undefined,
        budget: budget || undefined,
        shippingType: isLocalTransfer ? undefined : shippingType.trim() || undefined,
        contactName: contactName || undefined,
        contactEmail: contactEmail || undefined,
        contactPhone: contactPhone || undefined,
        address: address || undefined,
        comment: comment || undefined,
        // Initialize collections to empty arrays to avoid null reference errors in backend
        samples: samples.map((s) => ({ sampleId: s.sampleId, labelType: s.labelType })),
        sampleRequests: [],
        targetProjectId: targetProject?.id,
        // Date will be set by user later, or can be set to today
        // date: new Date(), // Optional - user can set later
      };

      log.debug('[AddShippingDialog] Creating shipping:', newShipping);

      // Create shipping via backend
      const createdShipping = await backend.shipping.create(newShipping);

      log.debug('[AddShippingDialog] Created shipping:', createdShipping);

      // If we have a selected node (should be the shippings container), add to tree
      if (selectedNode && selectedNode.type === 'shippings') {
        // Expand the shippings container if collapsed, so we can select the new node
        expandNode(selectedNode.nodeId);

        const newNodeId = await createNode('shipping', selectedNode.nodeId, createdShipping);

        if (newNodeId) {
          focusTreeNodeInEditMode(newNodeId);
        }
      }

      // Call onSubmit callback if provided
      if (onSubmit) {
        onSubmit(createdShipping);
      }

      showSnackbar(
        isLocalTransfer ? 'Sample transfer created successfully.' : 'Shipping created successfully.',
        'success'
      );

      const base = systemConfig?.basePath ?? '';
      if (createdShipping.id != null) {
        const idSeg = encodeURIComponent(createdShipping.id.toString());
        // From the global shippings list, use the canonical /shippings/:id route (resolves project + tree detail).
        if (launchFrom === 'shippingsPage') {
          router.push(`${base}/shippings/${idSeg}`);
        } else if (createdShipping.projectLabel) {
          router.push(`${base}/projects/${encodeURIComponent(createdShipping.projectLabel)}/shippings/${idSeg}`);
        } else {
          router.push(`${base}/shippings/${idSeg}`);
        }
      }

      handleClose();
    } catch (error: unknown) {
      log.error('[AddShippingDialog] Failed to create shipping:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create shipping.', 'error');
    }
  };

  const showRecipientTab = !isLocalTransfer;
  const hasAtLeastOneSample = samples.length >= 1;
  const hasTargetProjectWhenRequired = (project != null && isExternalProject(project)) || !!targetProject?.id;
  /** Shipping (non–local transfer) requires a label type per sample for the request payload. */
  const hasAllLabelTypesWhenRequired = useMemo(
    () => isLocalTransfer || samples.every((s) => String(s.labelType ?? '').trim() !== ''),
    [isLocalTransfer, samples]
  );
  const availableShippingTypes = options.shippingTypes ?? [];

  const canSubmitShipping =
    hasAtLeastOneSample &&
    budget.trim().length > 0 &&
    shippingType.trim().length > 0 &&
    researcherPhone.trim().length > 0 &&
    returnAddress.trim().length > 0 &&
    contactName.trim().length > 0 &&
    contactEmail.trim().length > 0 &&
    contactPhone.trim().length > 0 &&
    address.trim().length > 0;

  const submitDisabled =
    !hasAtLeastOneSample ||
    !hasTargetProjectWhenRequired ||
    (!isLocalTransfer && !hasAllLabelTypesWhenRequired) ||
    (tab === 1 && !canSubmitShipping);

  const submitTooltip = useMemo(() => {
    if (!submitDisabled) return undefined;
    if (tab === 0) {
      const parts: string[] = [];
      if (!hasAtLeastOneSample) parts.push('Add at least one sample.');
      if (!hasTargetProjectWhenRequired) parts.push('Choose a target project.');
      if (!isLocalTransfer && !hasAllLabelTypesWhenRequired) parts.push('Select a label type for each sample.');
      return parts.length > 0 ? parts.join(' ') : undefined;
    }
    if (tab === 1 && !canSubmitShipping) {
      return 'Fill in all required sender and recipient fields on this tab.';
    }
    return undefined;
  }, [
    submitDisabled,
    tab,
    hasAtLeastOneSample,
    hasTargetProjectWhenRequired,
    isLocalTransfer,
    hasAllLabelTypesWhenRequired,
    canSubmitShipping,
  ]);

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title={isLocalTransfer ? 'Create Sample Transfer' : 'Create Shipping'}
      maxWidth="lg"
      onSubmit={tab === 0 && showRecipientTab ? () => setTab(1) : handleSubmit}
      submitText={tab === 0 && showRecipientTab ? 'Next' : isLocalTransfer ? 'Create Transfer' : 'Create Shipping'}
      submitDisabled={submitDisabled}
      submitTooltip={submitTooltip}
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {project && isCurrentProjectExternal ? (
              <>
                Add samples from <strong>{project.projectLabel}</strong>. The {pendingRequestLabel} is created on this
                project.
              </>
            ) : !project ? (
              <>
                You opened this from the shippings page without a pre-selected project. Choose a{' '}
                <strong>target project</strong> below, then add samples. The {pendingRequestLabel} is filed on that
                target; samples are consolidated there when they are not already on that project. Sample lookup starts
                with no project filter so you can find any sample you can access.
              </>
            ) : (
              <>
                Choose a <strong>target project</strong> below (it defaults to the project you opened from). Samples not
                already on the target are moved there before the {pendingRequestLabel} is saved.
                {launchFrom === 'shippingsPage' ? (
                  <>
                    {' '}
                    Sample lookup starts without a project filter so you can find any sample you can access; adjust
                    filters in the picker as needed.
                  </>
                ) : (
                  <>
                    {' '}
                    Sample lookup starts scoped to <strong>{project.projectLabel}</strong>; you can change filters in
                    the picker to pull samples from other projects.
                  </>
                )}
              </>
            )}
          </Typography>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Select Samples" />
          <Tab label="Enter Recipient" disabled={isLocalTransfer} />
        </Tabs>

        {tab === 0 ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <ShippingSampleSelector
              projectLabel={project?.projectLabel ?? ''}
              shippingDialogLaunchFrom={launchFrom}
              samples={samples}
              onSamplesChange={setSamples}
              isLocalTransfer={isLocalTransfer}
              isReadOnly={false}
              isPending={true}
              isSubmitted={false}
              availableLabelTypes={options.shippingLabelTypes ?? []}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isLocalTransfer}
                  onChange={(e) => {
                    setIsLocalTransfer(e.target.checked);
                    if (e.target.checked) setTab(0);
                    markAsInteracted();
                  }}
                />
              }
              label="Local Transfer (instead of shipping)"
            />

            {isLocalTransfer ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Comment
                </Typography>
                <RichTextEditor
                  value={comment}
                  onChange={(value) => {
                    setComment(value);
                    markAsInteracted();
                  }}
                />
              </Box>
            ) : null}

            {!isCurrentProjectExternal ? (
              <>
                <FormControl component="fieldset">
                  <Typography variant="subtitle2" gutterBottom>
                    Recipient
                  </Typography>
                  <RadioGroup
                    row
                    value={recipientType}
                    onChange={(e) => {
                      const next = e.target.value as RecipientType;
                      setRecipientType(next);
                      if (next === 'inHouse') {
                        setTargetProject(
                          project?.id != null && project.projectLabel
                            ? { id: project.id, label: project.projectLabel }
                            : null
                        );
                      } else {
                        setTargetProject(null);
                      }
                      markAsInteracted();
                    }}
                  >
                    <FormControlLabel value="inHouse" control={<Radio />} label="In-House collaboration" />
                    <FormControlLabel value="externalUser" control={<Radio />} label="User" />
                  </RadioGroup>
                </FormControl>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Target project
                  </Typography>
                  <EntityAutocompleteWithLookup
                    fullWidth
                    label={
                      recipientType === 'externalUser' ? 'External user project (target)' : 'Internal project (target)'
                    }
                    options={targetProjectOptions}
                    value={targetProject}
                    onChange={(value) => {
                      setTargetProject(value as { id: number; label: string } | null);
                      markAsInteracted();
                    }}
                    onLookupClick={handleTargetProjectLookup}
                    lookupTooltip={
                      recipientType === 'externalUser'
                        ? 'Search and select external user project'
                        : 'Search and select internal target project'
                    }
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    The {pendingRequestLabel} is filed on this target. Samples are transferred to the target first when
                    they are not already on that project.
                  </Typography>
                </Box>
              </>
            ) : null}

            {isLocalTransfer ? (
              <Typography variant="body2" color="text.secondary">
                Creates a local transfer record. No address needed.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Add recipient address on the next tab.
              </Typography>
            )}
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Button variant="text" onClick={() => setTab(0)} sx={{ alignSelf: 'flex-start', mb: 1 }}>
              ← Back to Select Samples
            </Button>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 280, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Sender
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Internal Order Number"
                    value={budget}
                    onChange={(e) => {
                      setBudget(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <ShippingTypeSelect
                    label="Shipping Type"
                    required
                    value={shippingType}
                    options={availableShippingTypes}
                    onChange={(value) => {
                      setShippingType(value);
                      markAsInteracted();
                    }}
                  />
                  <TextField
                    label="Phone"
                    value={researcherPhone}
                    onChange={(e) => {
                      setResearcherPhone(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Return Address"
                    value={returnAddress}
                    onChange={(e) => {
                      setReturnAddress(e.target.value);
                      markAsInteracted();
                    }}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter return address for the shipment"
                  />
                </Stack>
              </Paper>
              <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 280, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Ship To
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Contact Name"
                    value={contactName}
                    onChange={(e) => {
                      setContactName(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Contact Email"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                    type="email"
                  />
                  <TextField
                    label="Contact Phone"
                    value={contactPhone}
                    onChange={(e) => {
                      setContactPhone(e.target.value);
                      markAsInteracted();
                    }}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Shipping Address"
                    value={address}
                    onChange={(e) => {
                      setAddress(e.target.value);
                      markAsInteracted();
                    }}
                    multiline
                    minRows={4}
                    required
                    fullWidth
                    size="small"
                    placeholder="Enter recipient shipping address"
                  />
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Comment
              </Typography>
              <RichTextEditor
                value={comment}
                onChange={(value) => {
                  setComment(value);
                  markAsInteracted();
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </StandardDialog>
  );
};

const AddShippingDialog: FC<AddShippingDialogProps> = (props) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const { currentUser } = useAuthStore();
  const dialogInitKey = `${props.project?.id ?? ''}|${props.launchFrom ?? ''}|${currentUser?.id ?? 'anon'}|${
    currentUser?.selectedRole ?? ''
  }|${String(currentUser?.usingSignInAs ?? false)}`;

  if (optionsLoading) {
    return (
      <StandardDialog
        open={props.open}
        onClose={props.onClose}
        title="Add Shipping / Transfer"
        maxWidth="lg"
        onSubmit={() => {}}
        submitText="Continue"
        submitDisabled
      >
        <Box sx={{ p: 2 }}>Loading options...</Box>
      </StandardDialog>
    );
  }

  return <AddShippingDialogInner key={dialogInitKey} {...props} options={options} />;
};

export { AddShippingDialog };
